import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import Pagination from '@/components/common/Pagination';
import Card from '@/components/UI/Card';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { PageInfo } from '@/interfaces/common.interface';
import { GetFilterUrl } from '@/utility/helper';
import React, { useEffect, useState } from 'react';
import FormField from '../UI/FormField';
import ImagePreview from '../common/media/ImagePreview';
import PageHeading from '../common/PageHeading';
import {
  IBrandsResponse,
  IBrandsResponseList,
} from '@/interfaces/brandsResponse.interface';

interface BrandTableProp {
  onSelectBrand?: (selected: IBrandsResponse[]) => void;
  selectionMode?: 'single' | 'multiple';
  initialSelectedIds?: Array<string | number>;
}

interface BrandStates {
  isLoading: boolean;
  brandStatus: string;
  brands: IBrandsResponse[];
  pageInfo: PageInfo;
}

const BrandTable: React.FC<BrandTableProp> = ({
  onSelectBrand,
  selectionMode = 'multiple',
  initialSelectedIds = [],
}) => {
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);

  const [states, setStates] = useState<BrandStates>({
    isLoading: false,
    brandStatus: 'active',
    brands: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  const handleChangeState = (newState: Partial<BrandStates>) => {
    setStates((prev) => ({ ...prev, ...newState }));
  };

  /* ------------------ API ------------------ */
  const fetchBrands = async () => {
    try {
      handleChangeState({ isLoading: true });

      const query = GetFilterUrl({
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: states.brandStatus,
        search: debounceValue,
      });

      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}${
        query ? `?${query}` : ''
      }`;

      const res = await callApi<IBrandsResponseList>(PATH, { method: 'GET' });

      handleChangeState({
        brands: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      console.error(error);
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchBrands();
  }, [
    states.pageInfo.page,
    states.pageInfo.limit,
    states.brandStatus,
    debounceValue,
  ]);

  /* ------------------ Selection ------------------ */
  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.brands);

  const handleSelectBrand = (brand: IBrandsResponse) => {
    if (selectionMode === 'single') {
      handleSelectAll(false);
      handleSelectItem(brand, true);
    } else {
      const isSelected = selectedItems.has(brand.id);
      handleSelectItem(brand, !isSelected);
    }
  };

  /* Emit selection to parent */
  useEffect(() => {
    const selectedData = states.brands.filter((c) => selectedItems.has(c.id));
    onSelectBrand?.(selectedData);
  }, [selectedItems, states.brands]);

  /* Initial selection (single or multiple) */
  useEffect(() => {
    if (initialSelectedIds.length === 0 || states.brands.length === 0) return;

    handleSelectAll(false);

    const idsToSelect =
      selectionMode === 'single'
        ? initialSelectedIds.slice(0, 1)
        : initialSelectedIds;

    states.brands.forEach((brand) => {
      if (idsToSelect.includes(brand.id)) {
        handleSelectItem(brand, true);
      }
    });
  }, [initialSelectedIds, states.brands]);

  /* ------------------ Columns ------------------ */
  const columns: TableColumn<IBrandsResponse>[] = [
    {
      key: 'name',
      header: 'Brand',
      render: (item) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${item?.media?.[0]?.url}`}
          alt=""
          text={item.name}
        />
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (item) => (
        <span className="inline-block max-w-[250px] truncate font-medium">
          {item.description}
        </span>
      ),
    },
    {
      key: 'productCount',
      header: 'Products',
    },
  ];

  /* ------------------ Pagination ------------------ */
  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: { ...states.pageInfo, page },
    });
  };

  const handleSizeChange = (limit: number) => {
    handleChangeState({
      pageInfo: { ...states.pageInfo, limit },
    });
  };

  /* ------------------ UI ------------------ */
  return (
    <Card>
      <PageHeading text="Brands" />
      <div className="mb-2">
        <FormField
          placeholder="Search for the brands..."
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>

      <CommonTable
        columns={columns}
        data={states.brands}
        showCheckbox
        selectedItems={selectedItems}
        onSelectAll={selectionMode === 'multiple' ? handleSelectAll : undefined}
        onSelectItem={handleSelectBrand}
        getItemId={(item) => item.id}
        emptyMessage="No Brands Found"
        loading={states.isLoading}
      />

      <Pagination
        paginationData={states.pageInfo}
        onPageChange={handleChangePage}
        isLoading={states.isLoading}
        onPageSizeChange={handleSizeChange}
      />
    </Card>
  );
};

export default BrandTable;
